/**
 * The router options that decide what a user sees when a route goes wrong.
 *
 * Split out of `main.tsx` so they can be asserted. `main.tsx` calls
 * `createRoot`, so a test cannot import it, which meant the two lines that
 * matter most here were the two lines nothing could pin: delete
 * `defaultErrorComponent` and every route crash silently falls back to
 * TanStack's own unstyled component, reporting nothing, and no test anywhere
 * would notice. A route-error test that builds its own router cannot catch
 * that either — it passes the option in itself.
 *
 * The route tree is deliberately *not* here. It is generated, it is large, and
 * keeping it out means a test of these defaults costs nothing to load.
 */

import { RouteNotFound } from '@/components/not-found-page';
import { RouteError } from '@/components/route-error';

export const routerDefaults = {
  /** Prefetch on hover/focus, so a click lands on an already-loaded route. */
  defaultPreload: 'intent',
  scrollRestoration: true,
  /**
   * The element the router must scroll back to the top on a fresh navigation.
   *
   * The shell is a viewport-sized app, not a document: `html, body, #root` are
   * `overflow: hidden` (see `styles.css`), and the only thing that scrolls is
   * the `<main>` in `app-shell.tsx`. The router's own reset-to-top step force
   * scrolls `window` plus whatever this list matches, and scrolling a window
   * that cannot scroll is a no-op — so without this, `main.scrollTop` carried
   * over from the previous route and a brewer landed halfway down a page they
   * had never opened.
   *
   * Restoring *back* to a remembered position already worked, because the
   * router tracks any element that fires a scroll event. Only the fresh-arrival
   * reset was missing.
   *
   * `as string[]` because the router wants a mutable array and this object is
   * `as const`.
   */
  scrollToTopSelectors: ['[data-slot="app-main"]'] as string[],
  /** An unknown URL, or a route that calls `notFound()`. */
  defaultNotFoundComponent: RouteNotFound,
  /**
   * Anything thrown inside a route. The router's catch boundary reaches this
   * before the `ErrorBoundary` outside the providers ever sees the throw.
   */
  defaultErrorComponent: RouteError,
} as const;
